import {
  SELECTABLE_VOID_REASONS,
  VOID_NOTE_MAX,
  VOID_REASON_LABELS,
  type SelectableVoidReason,
} from '@barso/shared';
import { useState } from 'react';
import { ReasonChips } from '../components/ReasonChips';
import { Button, TextField } from '../components/ui';
import { euro } from '../format';
import type { LocalSale } from '../pos/types';
import { voidReasonLine } from '../pos/voidText';
import { Sheet } from './posParts';

/** Antwort der Rückfrage: Grund (Pflicht beim Stornieren) und freiwilliger Text. */
export interface VoidAnswer {
  reason: SelectableVoidReason;
  note?: string;
}

const OPTIONS = SELECTABLE_VOID_REASONS.map((reason) => ({
  value: reason,
  label: VOID_REASON_LABELS[reason],
}));

/**
 * Rückfrage vor dem Stornieren (statt `window.confirm`): großer Rückgabebetrag, Grund (Pflicht) als sechs Chips, ein Text bis
 * 200 Zeichen (bei „Anderer Grund“ Pflicht), „Stornieren“ und „Abbrechen“. Eine Frage: Tipp auf den Hintergrund oder Wischen
 * schließen sie nicht. „Korrigieren“ (K5) fragt keinen Grund ab, die Kasse setzt ihn selbst (`correction`).
 */
export function VoidConfirmSheet({
  sale,
  lineNo,
  correct = false,
  refundCents,
  onAnswer,
}: {
  sale: LocalSale;
  lineNo?: number | undefined;
  /** „Korrigieren“ (K5): Der Verkauf wird storniert, die Artikel kommen in den Warenkorb zurück. */
  correct?: boolean;
  refundCents: number;
  onAnswer: (yes: boolean, answer?: VoidAnswer) => void;
}) {
  const [reason, setReason] = useState<SelectableVoidReason | null>(null);
  const [note, setNote] = useState('');
  const what =
    lineNo === undefined
      ? `Verkauf Nr. ${String(sale.registerSeq)}`
      : `Position ${String(lineNo)} von Verkauf Nr. ${String(sale.registerSeq)}`;
  const open = sale.lines.filter((line) => !line.voided).length;
  const text = note.trim();
  const noteRequired = reason === 'other';
  const ready = correct || (reason !== null && (!noteRequired || text !== ''));
  return (
    <Sheet
      title={correct ? `${what} korrigieren?` : `${what} stornieren?`}
      role="alertdialog"
      onClose={() => {
        onAnswer(false);
      }}
    >
      <span>{`Verkauf über ${euro(sale.totalCents)}`}</span>
      {correct ? (
        <>
          <strong>{`Nr. ${String(sale.registerSeq)} wird storniert, die ${String(open)} Artikel kommen zurück in den Warenkorb. Danach neu kassieren.`}</strong>
          <span>Schon bezahlt:</span>
        </>
      ) : sale.payment === 'card' ? (
        <strong>
          Mit Karte bezahlt: Erstattung am eigenen Kartengerät des Vereins, nicht aus der Kasse.
        </strong>
      ) : (
        <span>An den Kunden zurückgeben:</span>
      )}
      <p className="cash__sum cash__sum--sheet">{euro(refundCents)}</p>
      {!correct && (
        <>
          <ReasonChips
            label="Grund (Pflicht)"
            options={OPTIONS}
            value={reason}
            onChange={setReason}
          />
          <TextField
            label={noteRequired ? 'Anmerkung (Pflicht)' : 'Anmerkung (freiwillig)'}
            hint="Bitte keine Namen oder Kontaktdaten."
            value={note}
            maxLength={VOID_NOTE_MAX}
            autoComplete="off"
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          <span className="reason__counter" aria-hidden="true">
            {`${String(Array.from(note).length)}/${String(VOID_NOTE_MAX)}`}
          </span>
        </>
      )}
      <div className="row">
        <Button
          variant={correct ? 'primary' : 'danger'}
          disabled={!ready}
          onClick={() => {
            if (correct) {
              onAnswer(true);
              return;
            }
            if (reason === null) return;
            onAnswer(true, { reason, ...(text === '' ? {} : { note: text }) });
          }}
        >
          {correct ? 'Korrigieren' : 'Stornieren'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onAnswer(false);
          }}
        >
          Abbrechen
        </Button>
      </div>
      {!correct && !ready && (
        <p className="muted" role="status">
          {reason === null
            ? 'Bitte zuerst einen Grund wählen.'
            : 'Bei „Anderer Grund“ bitte kurz beschreiben.'}
        </p>
      )}
    </Sheet>
  );
}

/** Eine Zeile „Grund: Doppelt erfasst“ für storniert angezeigte Verkäufe (Verkäufe-Blatt, Letzter Verkauf). */
export function VoidReasonNote({ sale }: { sale: LocalSale }) {
  const text = voidReasonLine(sale);
  if (text === null) return null;
  return <span className="muted">{text}</span>;
}
